import React, { useEffect } from "react";

/**
 * DS ConfirmPopup — 판매채널 확인 팝업 "Pop up" 인스턴스 (figma 4279:146523 / 4279:146517 / 4116:134828).
 *
 * 구조: 헤더(좌 X 24 · 가운데 제목 Bold 20 · 우 24 spacer, py18 px20, 하단 1px 구분선)
 *       → 본문(py24 px20, 세로 gap 10: 굵은 첫 줄 SemiBold 18/28 · 보조 Regular 16/26)
 *       → 버튼 영역(py12 px20, 우측 정렬 gap 6: 취소 paper / 확정 틴트).
 * 오버레이는 .ds-modal-backdrop 을 그대로 쓴다 — backdrop 클릭으로는 닫지 않고 X·ESC 로만 닫는다(DSModal/DSDialog 와 동일).
 * 비포털이라 다른 DS 모달 위에 띄울 때는 호출부 래퍼에서 `> .ds-modal-backdrop` z-index 를 올린다.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.lead] - 본문 굵은 첫 줄
 * @param {string|React.ReactNode} [props.description] - 보조 줄. string 은 \n 줄바꿈 유지.
 *   node 는 래퍼가 display:contents 라 자식 하나하나가 본문 gap 10 의 형제가 된다(4116:134828 불릿 행).
 *   불릿은 <ul className="ds-confirm-popup__bullets"> 로 준다.
 *   설명+불릿이 한 묶음(gap 2)인 시안(4111:133813)은 <div className="ds-confirm-popup__group"> 으로 감싼다.
 * @param {string|null} [props.cancelText="취소"] - null 이면 취소 버튼 없이 확정 버튼 1개 (4111:133840 "확인")
 * @param {React.ReactNode} props.confirmText
 * @param {"blue"|"red"} [props.tone="blue"] - 확정 버튼 틴트 (blue=Icon/Blue 15% · red=Icon/Red 15%)
 * @param {boolean} [props.loading=false] - 확정 버튼 disabled + 라벨 자리에 .primary-loader 스피너(크기 유지)
 * @param {boolean} [props.cancelDisabled=false] - 취소 버튼만 막는다. X·ESC 는 onClose 로 따로 제어
 * @param {() => void} props.onCancel - 취소 버튼
 * @param {() => void} [props.onClose] - X·ESC. 생략 시 onCancel
 * @param {() => void} props.onConfirm
 * @param {number} [props.width=551]
 */
const DSConfirmPopup = ({
  open,
  title,
  lead,
  description,
  cancelText = "취소",
  confirmText,
  tone = "blue",
  loading = false,
  cancelDisabled = false,
  onCancel,
  onClose,
  onConfirm,
  width = 551,
}) => {
  const handleClose = onClose || onCancel;

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") handleClose && handleClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, handleClose]);

  if (!open) return null;

  const hasDescription =
    description !== undefined && description !== null && description !== "";

  return (
    <div className="ds-modal-backdrop">
      <div
        className="ds-confirm-popup"
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
      >
        <div className="ds-confirm-popup__header">
          <button
            type="button"
            className="ds-confirm-popup__close"
            onClick={handleClose}
            aria-label="닫기"
          />
          <span className="ds-confirm-popup__title">{title}</span>
          <span className="ds-confirm-popup__spacer" aria-hidden />
        </div>

        {lead || hasDescription ? (
          <div className="ds-confirm-popup__body">
            {lead ? <p className="ds-confirm-popup__lead">{lead}</p> : null}
            {hasDescription ? (
              typeof description === "string" ? (
                <p className="ds-confirm-popup__desc">{description}</p>
              ) : (
                <div className="ds-confirm-popup__desc ds-confirm-popup__desc--node">
                  {description}
                </div>
              )
            ) : null}
          </div>
        ) : null}

        <div className="ds-confirm-popup__actions">
          {cancelText === null ? null : (
            <button
              type="button"
              className="ds-confirm-popup__btn ds-confirm-popup__btn--cancel"
              disabled={cancelDisabled}
              onClick={onCancel}
            >
              {cancelText}
            </button>
          )}
          <button
            type="button"
            className={`ds-confirm-popup__btn ds-confirm-popup__btn--${
              tone === "red" ? "red" : "blue"
            }${loading ? " ds-confirm-popup__btn--loading" : ""}`}
            disabled={loading}
            aria-busy={loading || undefined}
            onClick={onConfirm}
          >
            <span className="ds-confirm-popup__btn-label">{confirmText}</span>
            {loading ? (
              <span
                className="primary-loader ds-confirm-popup__loader"
                aria-hidden
              />
            ) : null}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * DS ProgressPopup — 같은 "Pop up" 셸의 loading 변형 (figma 4170:135299 property1=loading, 인스턴스 4170:135351).
 *
 * 구조: 헤더(ConfirmPopup 과 동일) → 본문(flex-1, py24 px20, 가운데 정렬 gap 24: 로딩 이미지 · 안내 Regular 16/26 Text/Secondary).
 *       버튼 영역 없음. 프레임 높이 261 고정.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.description] - string 은 \n 줄바꿈 유지
 * @param {string} props.image - 로딩 이미지 src (시안 61×30)
 * @param {() => void} props.onClose - X·ESC
 * @param {number} [props.width=551]
 */
export const DSProgressPopup = React.memo(
  ({ open, title, description, image, onClose, width = 551 }) => {
    useEffect(() => {
      if (!open) return undefined;
      const handler = (e) => {
        if (e.key === "Escape") onClose && onClose();
      };
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    }, [open, onClose]);

    if (!open) return null;

    return (
      <div className="ds-modal-backdrop">
        <div
          className="ds-confirm-popup ds-confirm-popup--progress"
          style={{ width }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-busy="true"
          aria-label={typeof title === "string" ? title : undefined}
        >
          <div className="ds-confirm-popup__header">
            <button
              type="button"
              className="ds-confirm-popup__close"
              onClick={onClose}
              aria-label="닫기"
            />
            <span className="ds-confirm-popup__title">{title}</span>
            <span className="ds-confirm-popup__spacer" aria-hidden />
          </div>
          <div className="ds-confirm-popup__progress">
            <img className="ds-confirm-popup__progress-image" src={image} alt="" />
            {description ? (
              <p className="ds-confirm-popup__progress-desc">{description}</p>
            ) : null}
          </div>
        </div>
      </div>
    );
  },
);

export default React.memo(DSConfirmPopup);
